import {NgTemplateOutlet} from '@angular/common';
import {Component, computed, effect, ElementRef, Input, OnInit, signal, viewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {IonButton, IonContent, IonIcon, IonSpinner, IonToolbar, ModalController} from '@ionic/angular/standalone';
import {TranslocoModule, TranslocoService, provideTranslocoScope} from '@jsverse/transloco';
import {firstValueFrom} from 'rxjs';

import {
  classificaErroreCollegamento,
  CollegamentoAccountApiService,
  CollegamentoErrore,
  CollegamentoStatoResponse,
  ProviderIdentita,
} from '../../../services/collegamento-account/collegamento-account.api';

/** Ruoli con cui la modale si chiude: tutto il resto (gesture, backdrop, Esc) viene rifiutato. */
export type CollegamentoEsito = 'esci' | 'login' | 'riaccedi';
export const COLLEGAMENTO_ESITI: readonly CollegamentoEsito[] = ['esci', 'login', 'riaccedi'];

export type CollegamentoVista =
  | 'iniziale'
  | 'conferma'
  | 'inCorso'
  | 'collegato'
  | 'erroreCollegamento'
  | 'segnalazione'
  | 'riepilogo1'
  | 'riepilogo2'
  | 'inviata'
  | 'giaInviata'
  | 'sessioneScaduta'
  | 'nonDisponibile';

export type TipoErroreCollegamento = 'rete' | 'nonPossibile' | 'troppeRichieste';

const TELEFONO_PATTERN = /^\+?[0-9 ]{6,20}$/;
const CAMPI_FORM = ['nome', 'cognome', 'telefono', 'email', 'testo'] as const;
type CampoForm = (typeof CAMPI_FORM)[number];
/** Lunghezze massime (allineate al BE): usate sia dai validatori sia dagli attributi `maxlength` nativi. */
export const LUNGHEZZE_MAX = {nome: 100, cognome: 100, email: 254, testo: 1000} as const;

/** Rifiuta il testo fatto di soli spazi, che `Validators.required` lascerebbe passare. */
function nonVuoto(control: AbstractControl): ValidationErrors | null {
  const v = control.value;
  return typeof v === 'string' && v.length > 0 && v.trim().length === 0 ? {required: true} : null;
}

/**
 * Modale unica del collegamento SPID/CIE → account esistente. Un solo segnale `vista` governa
 * tutto il percorso; a ogni cambio vista il focus passa al titolo (WCAG 2.4.3), così lo screen
 * reader annuncia la nuova schermata.
 *
 * Riceve tutto via `componentProps` (token compreso: le chiamate sono pubbliche, il token va nel
 * body) e si chiude solo con i ruoli di {@link CollegamentoEsito}: le azioni locali (pulizia
 * storage, navigazione, logout, nuovo login) le esegue `CollegamentoAccountService` alla chiusura.
 */
@Component({
  selector: 'app-collegamento-account-modal',
  templateUrl: './collegamento-account-modal.component.html',
  styleUrls: ['./collegamento-account-modal.component.scss'],
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    IonContent,
    IonToolbar,
    IonSpinner,
    IonButton, IonIcon,
    TranslocoModule,
  ],
  providers: [provideTranslocoScope('auth')],
})
export class CollegamentoAccountModalComponent implements OnInit {
  /** Letto da `AutocloseOverlaysService`: il back/la navigazione non devono chiudere la modale. */
  @Input() isNotDismissableWithBack = true;
  @Input() accessToken = '';
  @Input() providerIniziale: ProviderIdentita = 'SPID';
  @Input() stato: CollegamentoStatoResponse | null = null;
  /** Errore della chiamata /stato iniziale (se è fallita): la modale parte in errore. */
  @Input() erroreStato: CollegamentoErrore | null = null;
  @Input() nomeIniziale = '';
  @Input() cognomeIniziale = '';
  /** Email e cellulare dall'identità digitale, se l'IdP li ha inviati: proposte modificabili. */
  @Input() emailIniziale = '';
  @Input() telefonoIniziale = '';

  readonly vista = signal<CollegamentoVista>('iniziale');
  readonly provider = signal<ProviderIdentita>('SPID');
  readonly statoRisposta = signal<CollegamentoStatoResponse | null>(null);
  readonly accountAttivato = signal(false);
  readonly tipoErrore = signal<TipoErroreCollegamento>('rete');
  /** Chiave i18n (sotto `auth.collegamento.`) dell'errore mostrato nelle viste di invio. */
  readonly erroreInvio = signal<string | null>(null);
  readonly invioInCorso = signal(false);

  /** `true` se la conferma è stata raggiunta da `giaInviata`: «Indietro» vi ritorna e la conferma ritira la segnalazione. */
  readonly daSegnalazione = signal(false);

  readonly riferimento = signal('');
  readonly telefonoInviato = signal('');
  readonly segnalazioneInviataIl = signal<string | null>(null);

  readonly form = new FormGroup({
    nome: new FormControl('', {nonNullable: true, validators: [Validators.required, nonVuoto, Validators.maxLength(LUNGHEZZE_MAX.nome)]}),
    cognome: new FormControl('', {nonNullable: true, validators: [Validators.required, nonVuoto, Validators.maxLength(LUNGHEZZE_MAX.cognome)]}),
    telefono: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.pattern(TELEFONO_PATTERN)]}),
    email: new FormControl('', {nonNullable: true, validators: [Validators.email, Validators.maxLength(LUNGHEZZE_MAX.email)]}),
    testo: new FormControl('', {nonNullable: true, validators: [Validators.required, nonVuoto, Validators.maxLength(LUNGHEZZE_MAX.testo)]}),
  });

  readonly lunghezzeMax = LUNGHEZZE_MAX;

  /** Lunghezza corrente del messaggio, per il contatore. */
  readonly lunghezzaTesto = signal(0);
  /** Campi errati all'ultimo "Continua" non valido (0 = nessun riepilogo errori da mostrare). */
  readonly campiDaCorreggere = signal(0);

  /**
   * Testo della regione `role="status"` sempre presente nel DOM (un live region inserito insieme al
   * proprio contenuto spesso non viene annunciato): vuoto quando non c'è un invio in corso.
   */
  readonly messaggioProgresso = computed(() =>
    this.vista() === 'inCorso' || this.invioInCorso() ? this.transloco.translate('auth.collegamento.inCorso.testo') : '',
  );

  readonly nomeCompleto = computed(() => {
    const a = this.statoRisposta()?.account;
    return a ? [a.nome, a.cognome].filter((s) => !!s).join(' ') : '';
  });
  readonly emailMascherata = computed(() => this.statoRisposta()?.account?.email_mascherata ?? null);
  /** Dominio dell'email mascherata, per la lettura vocale (gli asterischi sono illeggibili). */
  readonly dominioEmail = computed(() => {
    const e = this.emailMascherata();
    const i = e ? e.indexOf('@') : -1;
    return i >= 0 ? e!.slice(i + 1) : null;
  });

  readonly riepilogo = computed(() => {
    const v = this.form.getRawValue();
    return {
      nome: v.nome.trim(),
      cognome: v.cognome.trim(),
      telefono: v.telefono.trim(),
      email: v.email.trim(),
      testo: v.testo.trim(),
    };
  });

  readonly dataSegnalazione = computed(() => {
    const iso = this.segnalazioneInviataIl();
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(this.transloco.getActiveLang() || 'it', {day: 'numeric', month: 'long', year: 'numeric'});
  });

  /** Heading `h1` nativo (tabindex=-1): `viewChild` su un elemento nativo dà un `ElementRef`. */
  private readonly titolo = viewChild<ElementRef<HTMLElement>>('titolo');
  /** `true` finché /stato non è mai riuscita: "Riprova" deve rifare /stato, non /collega. */
  private statoMancante = false;

  constructor(
    private readonly modalController: ModalController,
    private readonly api: CollegamentoAccountApiService,
    private readonly transloco: TranslocoService,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    // Il focus segue la vista: titolo con tabindex=-1, quindi focalizzabile solo da codice.
    effect(() => {
      this.vista();
      this.spostaFocus();
    });
  }

  ngOnInit(): void {
    this.provider.set(this.stato?.provider ?? this.providerIniziale);
    this.statoRisposta.set(this.stato);
    this.form.patchValue({
      nome: this.nomeIniziale,
      cognome: this.cognomeIniziale,
      email: this.emailIniziale,
      // SPID invia il cellulare senza spazi (`+393331234567`): passa già il formato atteso.
      telefono: this.telefonoIniziale.trim(),
    });
    this.form.controls.testo.valueChanges.subscribe((v) => this.lunghezzaTesto.set(v.length));
    this.form.statusChanges.subscribe(() => {
      if (this.campiDaCorreggere() > 0 && this.form.valid) {
        this.campiDaCorreggere.set(0);
      }
    });

    if (this.erroreStato || !this.stato) {
      this.statoMancante = true;
      this.applicaErroreStato(this.erroreStato ?? 'altro');
      return;
    }
    this.applicaStato(this.stato);
  }

  // ---- stato iniziale ------------------------------------------------------------------------

  private applicaStato(s: CollegamentoStatoResponse): void {
    this.statoMancante = false;
    this.statoRisposta.set(s);
    if (s.provider) {
      this.provider.set(s.provider);
    }
    switch (s.stato) {
      case 'RICHIESTO':
        this.vista.set('iniziale');
        break;
      case 'SEGNALAZIONE_IN_CORSO':
        this.segnalazioneDaStato(s);
        break;
      case 'NON_DISPONIBILE':
        this.vista.set('nonDisponibile');
        break;
      case 'NON_RICHIESTO':
      default:
        // Il collegamento non serve più (già fatto): basta accedere di nuovo.
        this.vista.set('collegato');
        break;
    }
  }

  private applicaErroreStato(e: CollegamentoErrore): void {
    if (e === 'token_non_valido' || e === 'autenticazione_scaduta') {
      this.vista.set('sessioneScaduta');
      return;
    }
    this.tipoErrore.set(e === 'troppe_richieste' ? 'troppeRichieste' : 'rete');
    this.vista.set('erroreCollegamento');
  }

  private segnalazioneDaStato(s: CollegamentoStatoResponse): void {
    this.riferimento.set(s.segnalazione?.riferimento ?? '');
    this.segnalazioneInviataIl.set(s.segnalazione?.inviata_il ?? null);
    this.vista.set('giaInviata');
  }

  // ---- navigazione fra le viste --------------------------------------------------------------

  vai(vista: CollegamentoVista): void {
    // Durante l'invio la navigazione fra le viste è ignorata (il pulsante resta focalizzabile).
    if (this.invioInCorso()) {
      return;
    }
    if (vista !== 'conferma') {
      this.daSegnalazione.set(false);
    }
    this.erroreInvio.set(null);
    this.campiDaCorreggere.set(0);
    this.vista.set(vista);
  }

  /** «In realtà è il mio account» dalla vista `giaInviata`: alla conferma unica del collegamento. */
  vaiAConfermaDaSegnalazione(): void {
    if (this.invioInCorso()) {
      return;
    }
    this.erroreInvio.set(null);
    this.daSegnalazione.set(true);
    this.vista.set('conferma');
  }

  /** «Indietro» dalla conferma: torna alla vista da cui si era partiti. */
  indietroDaConferma(): void {
    this.vai(this.daSegnalazione() ? 'giaInviata' : 'iniziale');
  }

  // ---- collegamento --------------------------------------------------------------------------

  /** Conferma ("Collega l'account"): l'unico punto che chiama /collega. */
  async collega(): Promise<void> {
    // Doppio tocco / doppia conferma: durante la chiamata la vista è `inCorso`.
    if (this.vista() === 'inCorso') {
      return;
    }
    this.vista.set('inCorso');
    try {
      const r = await firstValueFrom(this.api.collega(this.accessToken));
      this.accountAttivato.set(!!r?.account_attivato);
      this.vista.set('collegato');
    } catch (err) {
      await this.gestisciErroreCollega(classificaErroreCollegamento(err));
    }
  }

  private async gestisciErroreCollega(e: CollegamentoErrore): Promise<void> {
    switch (e) {
      case 'non_piu_necessario':
        // Già collegato altrove: l'esito per l'utente è identico al successo.
        this.vista.set('collegato');
        return;
      case 'token_non_valido':
      case 'autenticazione_scaduta':
        this.vista.set('sessioneScaduta');
        return;
      case 'non_possibile':
        this.tipoErrore.set('nonPossibile');
        break;
      case 'troppe_richieste':
        this.tipoErrore.set('troppeRichieste');
        break;
      default:
        this.tipoErrore.set('rete');
    }
    this.vista.set('erroreCollegamento');
  }

  /** "Riprova" nella vista d'errore: rifà la chiamata che era fallita. */
  async riprova(): Promise<void> {
    if (this.statoMancante) {
      await this.ricaricaStato();
      return;
    }
    await this.collega();
  }

  private async ricaricaStato(): Promise<void> {
    this.vista.set('inCorso');
    try {
      this.applicaStato(await firstValueFrom(this.api.stato(this.accessToken)));
    } catch (err) {
      this.applicaErroreStato(classificaErroreCollegamento(err));
    }
  }

  /** Dopo un 409 di /segnalazione (già inviata) si rilegge /stato per avere riferimento e data. */
  private async mostraSegnalazioneEsistente(): Promise<void> {
    try {
      const s = await firstValueFrom(this.api.stato(this.accessToken));
      this.statoRisposta.set(s);
      if (s.segnalazione) {
        this.segnalazioneDaStato(s);
        return;
      }
    } catch {
      // senza dettagli si mostra comunque la vista "già inviata" nella forma generica
    }
    this.riferimento.set('');
    this.segnalazioneInviataIl.set(null);
    this.vista.set('giaInviata');
  }

  // ---- segnalazione --------------------------------------------------------------------------

  /** "Continua" dal form: se non valido, mostra gli errori e porta il focus sul primo campo errato. */
  vaiAlRiepilogo(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.campiDaCorreggere.set(CAMPI_FORM.filter((c) => this.form.controls[c].invalid).length);
      this.focalizzaPrimoErrore();
      return;
    }
    this.vai('riepilogo1');
  }

  /** Seconda conferma ("Invia la segnalazione"): l'unico punto che chiama /segnalazione. */
  async invia(): Promise<void> {
    if (this.invioInCorso()) {
      return;
    }
    this.invioInCorso.set(true);
    this.erroreInvio.set(null);
    const r = this.riepilogo();
    try {
      const res = await firstValueFrom(
        this.api.segnala({
          access_token: this.accessToken,
          nome: r.nome,
          cognome: r.cognome,
          telefono: r.telefono,
          ...(r.email ? {email: r.email} : {}),
          testo_segnalazione: r.testo,
        }),
      );
      this.riferimento.set(res.riferimento);
      if (res.gia_inviata) {
        this.segnalazioneInviataIl.set(null);
        this.vista.set('giaInviata');
      } else {
        this.telefonoInviato.set(r.telefono);
        this.vista.set('inviata');
      }
    } catch (err) {
      await this.gestisciErroreInvio(classificaErroreCollegamento(err));
    } finally {
      this.invioInCorso.set(false);
    }
  }

  private async gestisciErroreInvio(e: CollegamentoErrore): Promise<void> {
    switch (e) {
      case 'segnalazione_gia_inviata':
        await this.mostraSegnalazioneEsistente();
        return;
      case 'token_non_valido':
      case 'autenticazione_scaduta':
        this.vista.set('sessioneScaduta');
        return;
      case 'troppe_richieste':
        this.erroreInvio.set('errore.troppeRichieste');
        break;
      case 'validazione':
        // I dati non sono passati la validazione del BE: si torna al form per correggerli. Il
        // cambio vista porta già il focus sul messaggio d'errore.
        this.erroreInvio.set('errore.validazione');
        this.vista.set('segnalazione');
        return;
      default:
        this.erroreInvio.set('errore.invio');
    }
    // Si resta nella stessa vista: il focus va esplicitamente sul messaggio (dopo il rendering).
    this.spostaFocus();
  }

  // ---- errori del form -----------------------------------------------------------------------

  /** Chiave i18n dell'errore del campo, o `null`. Visibile solo dopo blur o submit (touched). */
  erroreCampo(campo: CampoForm): string | null {
    const c = this.form.controls[campo];
    if (!c.touched || !c.errors) {
      return null;
    }
    switch (campo) {
      case 'nome':
        return c.errors['maxlength'] ? 'segnalazione.errori.lunghezza' : 'segnalazione.errori.nome';
      case 'cognome':
        return c.errors['maxlength'] ? 'segnalazione.errori.lunghezza' : 'segnalazione.errori.cognome';
      case 'telefono':
        return c.errors['required'] ? 'segnalazione.errori.telefonoObbligatorio' : 'segnalazione.errori.telefonoNonValido';
      case 'email':
        return c.errors['maxlength'] ? 'segnalazione.errori.lunghezza' : 'segnalazione.errori.email';
      case 'testo':
        return c.errors['maxlength'] ? 'segnalazione.errori.lunghezza' : 'segnalazione.errori.messaggio';
    }
  }

  /** Parametri di interpolazione dell'errore del campo (`{{ max }}` del messaggio di lunghezza). */
  erroreParams(campo: CampoForm): {max?: number} {
    return campo === 'telefono' ? {} : {max: LUNGHEZZE_MAX[campo]};
  }

  /** `aria-describedby` del campo: suggerimenti fissi e messaggio d'errore (se visibile). */
  descrittori(campo: CampoForm, ...fissi: string[]): string | null {
    const ids = [...fissi];
    if (this.erroreCampo(campo)) {
      ids.push(`collegamento-errore-${campo}`);
    }
    return ids.length ? ids.join(' ') : null;
  }

  private focalizzaPrimoErrore(): void {
    const primo = CAMPI_FORM.find((c) => this.form.controls[c].invalid);
    if (!primo) return;
    // Dopo il rendering: messaggi e aria-describedby sono già nel DOM quando il campo riceve il focus.
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(`[data-campo="${primo}"]`)?.focus());
  }

  /**
   * Focus sul messaggio d'errore d'invio se presente (anche dopo un ritorno al form per errore di
   * validazione), altrimenti sul titolo della vista. Rimandato a dopo il rendering.
   */
  private spostaFocus(): void {
    setTimeout(() => {
      const errore = this.host.nativeElement.querySelector<HTMLElement>('#collegamento-errore-invio');
      (errore ?? this.titolo()?.nativeElement)?.focus();
    });
  }

  // ---- chiusura ------------------------------------------------------------------------------

  chiudi(esito: CollegamentoEsito): void {
    void this.modalController.dismiss(null, esito);
  }
}
